import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { getHomePage, type HomePageData } from "../api/homePageApi";

type HomePageStatus = "loading" | "ready" | "error";

interface HomePageContextValue {
  data: HomePageData | null;
  status: HomePageStatus;
}

const CACHE_KEY = "arunika_home_page";

const readCache = (): HomePageData | null => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);

    return raw ? (JSON.parse(raw) as HomePageData) : null;
  } catch {
    return null;
  }
};

const writeCache = (data: HomePageData) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Storage penuh / diblokir -> abaikan, cache hanya optimasi.
  }
};

const HomePageContext = createContext<HomePageContextValue>({
  data: null,
  status: "loading",
});

/**
 * Mengambil konten Home SEKALI untuk seluruh website (Home, Footer, tombol
 * WhatsApp, dst), bukan di tiap komponen.
 *
 * - Kunjungan pertama: status "loading" sampai API menjawab, supaya
 *   pengunjung tidak sempat melihat konten bawaan lalu berganti.
 * - Berikutnya (dalam sesi yang sama): langsung tampil dari cache sesi
 *   sambil diperbarui di belakang layar.
 * - API gagal: status "error" -> komponen memakai konten bawaan.
 */
export function HomePageProvider({ children }: { children: ReactNode }) {
  const [cached] = useState(readCache);

  const [data, setData] = useState<HomePageData | null>(cached);
  const [status, setStatus] = useState<HomePageStatus>(
    cached ? "ready" : "loading"
  );

  useEffect(() => {
    let cancelled = false;

    getHomePage()
      .then((result) => {
        if (cancelled) return;

        setData(result);
        setStatus("ready");
        writeCache(result);
      })
      .catch(() => {
        if (cancelled) return;

        // Kalau sudah ada cache, tetap pakai; kalau tidak -> konten bawaan.
        setStatus((prev) => (prev === "ready" ? prev : "error"));
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <HomePageContext.Provider value={{ data, status }}>
      {children}
    </HomePageContext.Provider>
  );
}

export const useHomePage = () => useContext(HomePageContext);
